// src/pages/Admin/AdminDashboard.js

import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import "../../styles/AdminDashboard.css";
import "../../styles/Tables.css";

import Skeleton from "../../components/Skeleton";

import {
  fetchAdminStats,
  fetchAdminClients,
  fetchAdminDefraStatus,
  downloadAdminDefraWorkbookApi,
  uploadAdminDefraWorkbookApi,
  adminImpersonate,
  adminResetPassword,
  downloadAdminReportApi,
} from "../../lib/api";

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [defraStatus, setDefraStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const [resetClient, setResetClient] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [defraFile, setDefraFile] = useState(null);
  const [defraInputKey, setDefraInputKey] = useState(0);

  const [downloading, setDownloading] = useState(false);
  const [defraDownloading, setDefraDownloading] = useState(false);
  const [defraUploading, setDefraUploading] = useState(false);

  // ✅ NEW: client search + sort
  const [clientQuery, setClientQuery] = useState("");
  const [sortBy, setSortBy] = useState("last_upload_desc"); // last_upload_desc | total_desc | uploads_desc | name_az

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [s, c, d] = await Promise.all([
          fetchAdminStats(),
          fetchAdminClients(),
          fetchAdminDefraStatus(),
        ]);
        setStats(s);
        setClients(c || []);
        setDefraStatus(d || null);
      } catch (err) {
        toast.error(err?.message || "Failed to load admin dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const defraReviewNote = useMemo(() => {
    if (!defraStatus?.exists) return "DEFRA workbook missing";
    const days = Number(defraStatus?.days_until_review);
    if (!Number.isFinite(days)) return "Review date unavailable";
    if (days < 0) return `Review overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`;
    if (days === 0) return "Review due today";
    return `Review due in ${days} day${days === 1 ? "" : "s"}`;
  }, [defraStatus]);

  const totals = useMemo(() => {
    const total = safeNumber(stats?.total_emissions_kgco2e);
    const s1 = safeNumber(stats?.scope_totals?.scope_1_kgco2e);
    const s2 = safeNumber(stats?.scope_totals?.scope_2_kgco2e);
    const s3 = safeNumber(stats?.scope_totals?.scope_3_kgco2e);
    return { total, s1, s2, s3 };
  }, [stats]);

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    let list = Array.isArray(clients) ? [...clients] : [];

    if (q) {
      list = list.filter((c) => {
        const name = String(c?.client || "").toLowerCase();
        const user = String(c?.username || "").toLowerCase();
        return name.includes(q) || user.includes(q);
      });
    }

    const byName = (x) => String(x?.client || "").toLowerCase();
    const byUploads = (x) => safeNumber(x?.upload_count);
    const byTotal = (x) => safeNumber(x?.total_kgco2e);
    const byLast = (x) => {
      const d = x?.last_upload ? new Date(x.last_upload) : null;
      const t = d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
      return t;
    };

    switch (sortBy) {
      case "total_desc":
        list.sort((a, b) => byTotal(b) - byTotal(a));
        break;
      case "uploads_desc":
        list.sort((a, b) => byUploads(b) - byUploads(a));
        break;
      case "name_az":
        list.sort((a, b) => byName(a).localeCompare(byName(b)));
        break;
      case "last_upload_desc":
      default:
        list.sort((a, b) => byLast(b) - byLast(a));
        break;
    }

    return list;
  }, [clients, clientQuery, sortBy]);

  const handleImpersonate = async (clientName) => {
    const ok = window.confirm(`View the platform as "${clientName}"? You can return by logging out.`);
    if (!ok) return;

    try {
      toast.loading(`Switching to ${clientName}…`, { id: "imp" });
      await adminImpersonate(clientName);
      toast.success("Impersonation active", { id: "imp" });
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err?.message || "Failed to impersonate", { id: "imp" });
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetClient) return toast.error("Choose a client");
    if (!newPassword) return toast.error("Enter a new password");

    try {
      toast.loading("Resetting password…", { id: "reset" });
      await adminResetPassword(resetClient, newPassword);
      toast.success("Password updated", { id: "reset" });
      setNewPassword("");
    } catch (err) {
      toast.error(err?.message || "Failed to reset password", { id: "reset" });
    }
  };

  const handleDownloadAdminReport = async () => {
    try {
      setDownloading(true);
      toast.loading("Preparing admin report…", { id: "adminpdf" });

      const blob = await downloadAdminReportApi();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "admin_report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

      toast.success("Admin report downloaded", { id: "adminpdf" });
    } catch (err) {
      toast.error(err?.message || "Admin report not available yet", { id: "adminpdf" });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadDefraWorkbook = async () => {
    try {
      setDefraDownloading(true);
      toast.loading("Preparing DEFRA workbook…", { id: "defra-download" });
      const blob = await downloadAdminDefraWorkbookApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defraStatus?.file_name || "defra_factors.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("DEFRA workbook downloaded", { id: "defra-download" });
    } catch (err) {
      toast.error(err?.message || "Failed to download DEFRA workbook", { id: "defra-download" });
    } finally {
      setDefraDownloading(false);
    }
  };

  const handleUploadDefraWorkbook = async () => {
    if (!defraFile) {
      toast.error("Choose a replacement DEFRA workbook first");
      return;
    }

    try {
      setDefraUploading(true);
      toast.loading("Validating and replacing DEFRA workbook…", { id: "defra-upload" });
      const data = await uploadAdminDefraWorkbookApi(defraFile);
      setDefraStatus(data?.status || null);
      setDefraFile(null);
      setDefraInputKey((v) => v + 1);
      toast.success("DEFRA workbook updated", { id: "defra-upload" });
    } catch (err) {
      toast.error(err?.message || "Failed to replace DEFRA workbook", { id: "defra-upload" });
    } finally {
      setDefraUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-hero">
          <div>
            <h1>Admin dashboard</h1>
            <p className="subtitle">Loading platform analytics…</p>
          </div>
        </div>

        <div className="admin-kpis">
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </div>

        <div className="admin-grid">
          <div className="admin-card">
            <Skeleton height={260} />
          </div>
          <div className="admin-card">
            <Skeleton height={260} />
          </div>
        </div>

        <div className="admin-card admin-section">
          <Skeleton height={140} />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* HERO */}
      <div className="admin-hero">
        <div>
          <h1>Admin dashboard</h1>
          <p className="subtitle">Platform-wide overview across all client organisations.</p>
        </div>

        <div className="admin-hero-actions">
          <button
            className="btn-secondary"
            onClick={handleDownloadAdminReport}
            disabled={downloading}
            title="Downloads a platform-wide report (requires backend endpoint)"
          >
            {downloading ? "Preparing…" : "Download admin report"}
          </button>
        </div>
      </div>

      {/* KPI ROW */}
      <div className="admin-kpis">
        <div className="card kpi">
          <div className="kpi-label">Total clients</div>
          <div className="kpi-value">{stats?.total_clients ?? 0}</div>
          <div className="kpi-small" style={{ color: "var(--muted)", marginTop: 6 }}>
            Active client folders
          </div>
        </div>

        <div className="card kpi">
          <div className="kpi-label">Total uploads</div>
          <div className="kpi-value">{stats?.total_uploads ?? 0}</div>
          <div className="kpi-small" style={{ color: "var(--muted)", marginTop: 6 }}>
            All documents
          </div>
        </div>

        <div className="card kpi" style={{ gridColumn: "span 2" }}>
          <div className="kpi-label">Total emissions</div>
          <div className="kpi-value">
            {Number(totals.total).toLocaleString()}
            <span className="kpi-unit">kg CO₂e</span>
          </div>
          <div className="kpi-small" style={{ color: "var(--muted)", marginTop: 6 }}>
            S1 {Number(totals.s1).toLocaleString()} • S2 {Number(totals.s2).toLocaleString()} • S3{" "}
            {Number(totals.s3).toLocaleString()}
          </div>
        </div>
      </div>

      {/* GRID: RECENT + CLIENTS */}
      <div className="admin-grid">
        {/* RECENT ACTIVITY */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>Recent activity</h3>
            <span className="pill pill-soft">Latest uploads</span>
          </div>

          {stats?.recent_activity?.length ? (
            <div className="table-card">
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Last upload</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_activity.map((r, idx) => (
                    <tr key={idx}>
                      <td>{r.client}</td>
                      <td>{formatDate(r.last_upload)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="admin-empty">
              <p className="admin-empty-title">No uploads recorded yet.</p>
              <p className="admin-empty-sub">Uploads will appear here as clients add evidence.</p>
            </div>
          )}
        </div>

        {/* CLIENTS */}
        <div className="admin-card">
          <div className="admin-card-header" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div>
              <h3>Clients</h3>
              <p className="subtitle" style={{ marginTop: 6, opacity: 0.8 }}>
                Search + sort to quickly find organisations.
              </p>
            </div>

            <span className="pill pill-soft">{filteredClients.length} shown</span>
          </div>

          {/* Controls */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
            <input
              type="text"
              value={clientQuery}
              onChange={(e) => setClientQuery(e.target.value)}
              placeholder="Search client / username…"
              style={{ flex: 1, minWidth: 220 }}
            />

            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ minWidth: 190 }}>
              <option value="last_upload_desc">Sort: Latest upload</option>
              <option value="total_desc">Sort: Total emissions</option>
              <option value="uploads_desc">Sort: Upload count</option>
              <option value="name_az">Sort: Name A–Z</option>
            </select>

            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                setClientQuery("");
                setSortBy("last_upload_desc");
              }}
              disabled={!clientQuery && sortBy === "last_upload_desc"}
            >
              Clear
            </button>
          </div>

          {/* ✅ Scrollable table so admin page doesn’t grow forever */}
          <div className="table-card">
            <div style={{ maxHeight: 420, overflowY: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Username</th>
                    <th>Uploads</th>
                    <th>Last upload</th>
                    <th>Total (kg CO₂e)</th>
                    <th style={{ width: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((c, idx) => (
                    <tr key={c.client || idx}>
                      <td>{c.client}</td>
                      <td>{c.username}</td>
                      <td>{safeNumber(c.upload_count)}</td>
                      <td>{formatDate(c.last_upload)}</td>
                      <td>{Number(safeNumber(c.total_kgco2e)).toLocaleString()}</td>
                      <td style={{ display: "flex", gap: 8 }}>
                        <button className="btn-primary" onClick={() => handleImpersonate(c.client)}>
                          View as client
                        </button>
                      </td>
                    </tr>
                  ))}

                  {filteredClients.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: 14, opacity: 0.8 }}>
                        No matching clients.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* RESET PASSWORD */}
      <div className="admin-card admin-section">
        <div className="admin-section-header">
          <h2>Reset client password</h2>
        </div>

        <form onSubmit={handleResetPassword} className="admin-form">
          <div className="admin-form-row">
            <select value={resetClient} onChange={(e) => setResetClient(e.target.value)}>
              <option value="">Select client…</option>
              {clients.map((c) => (
                <option key={c.client} value={c.client}>
                  {c.client}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <button className="btn-secondary" type="submit">
              Reset password
            </button>
          </div>
        </form>

        <div className="admin-divider" />

        <div className="admin-section-header">
          <div>
            <h2>DEFRA source file</h2>
            <p className="subtitle admin-inline-subtitle">
              This is the workbook currently used for manual-entry categories and upload calculations.
            </p>
          </div>
          <div className="admin-defra-actions">
            <span className={`pill ${defraStatus?.review_status === "due" ? "pill-warn" : "pill-soft"}`}>
              {defraReviewNote}
            </span>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleDownloadDefraWorkbook}
              disabled={defraDownloading || !defraStatus?.exists}
            >
              {defraDownloading ? "Downloading…" : "Download current file"}
            </button>
          </div>
        </div>

        <div className="admin-defra-grid">
          <div className="admin-defra-item">
            <span className="admin-defra-label">File in use</span>
            <strong>{defraStatus?.file_name || "defra_factors.xlsx"}</strong>
          </div>
          <div className="admin-defra-item">
            <span className="admin-defra-label">Last updated</span>
            <strong>{formatDate(defraStatus?.last_modified_at)}</strong>
          </div>
          <div className="admin-defra-item">
            <span className="admin-defra-label">Next review due</span>
            <strong>{formatDate(defraStatus?.review_due_at)}</strong>
          </div>
          <div className="admin-defra-item">
            <span className="admin-defra-label">Workbook coverage</span>
            <strong>
              {Number(defraStatus?.factor_count || 0).toLocaleString()} factors /{" "}
              {Number(defraStatus?.category_count || 0).toLocaleString()} categories
            </strong>
          </div>
        </div>

        <p className="admin-defra-footnote">
          When this review date is reached, replace the DEFRA workbook here. The upload is validated first and then
          swapped into the live DEFRA file path automatically, so you do not need to change backend files manually.
        </p>

        <div className="admin-defra-replace">
          <input
            key={defraInputKey}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setDefraFile(e.target.files?.[0] || null)}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={handleUploadDefraWorkbook}
            disabled={!defraFile || defraUploading}
          >
            {defraUploading ? "Replacing…" : "Replace DEFRA file"}
          </button>
        </div>
        {defraFile ? (
          <p className="admin-defra-selected">Selected replacement: {defraFile.name}</p>
        ) : null}
      </div>
    </div>
  );
}
